import debounce from 'lodash.debounce';
import { ReactElement, useCallback, useState } from 'react';
import { useNonInitialEffect } from 'src/utils/useNonInitialEffect';
import { useQueryParams } from 'src/utils/useQueryParams';

import Search from '../../Search/Search';
import { TableQueryParameters } from '../Table.interface';

export interface TableSearchProps<S, T> {
  disabled?: boolean;
  urlParams: TableQueryParameters<S, T>;
  placeholder?: string;
  className?: string;
  onSearchChange?: (search: string) => void;
}

const TableSearch = <S, T>({
  disabled = false,
  urlParams,
  placeholder,
  className,
  onSearchChange,
}: TableSearchProps<S, T>): ReactElement => {
  const [search, setSearch] = useState(urlParams?.q || '');

  const { updateURLWithQueryParams } = useQueryParams<typeof urlParams>(
    window.location.search,
    urlParams
  );

  const onSearchInput = (value: string) => {
    if (onSearchChange) {
      onSearchChange(value);
      return;
    }
    const updatedParams: typeof urlParams = {
      ...urlParams,
      page: 0,
      q: value,
    };
    updateURLWithQueryParams<typeof urlParams>(
      window.location.pathname,
      updatedParams
    );
  };

  //eslint-disable-next-line
  const delayedOnSearchInput = useCallback(debounce(onSearchInput, 500), [
    search,
  ]);

  useNonInitialEffect(() => {
    delayedOnSearchInput(search);
    return delayedOnSearchInput.cancel;
  }, [delayedOnSearchInput, search]);

  return (
    <Search
      disabled={disabled}
      placeholder={placeholder}
      value={search}
      onChange={(v) => setSearch(v)}
      className={className}
    />
  );
};

export default TableSearch;
